import React, { useEffect, useMemo, useState } from 'react';
import PageWrapper from '../../components/PageWrapper';
import { useAuthEffects } from '../../components/Auth';
import { Box, Typography, Chip, Collapse, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { useAppSelector } from '../../hooks';
import dayjs from 'dayjs';
import { selectSalesOrdersListItems } from './salesSlice';
import { useSalesOrderListPublicMutation } from '../../services/salesOrders';
import { ListViewStatusStepper } from '../orders/components/Stepper';
import { CURRENCY_SYMBOLS, OrderStatus } from '../../types';
import { translateSalesOrderStatus } from '../../i18n/utils';
import { SimpleList } from '../../components/SimpleList';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const SalesListView = () => {
	useAuthEffects();

	const [salesOrderListPublicMutation] = useSalesOrderListPublicMutation();
	const orders = useAppSelector(selectSalesOrdersListItems);
	const [showInactive, setShowInactive] = useState<boolean>(false);
	const { t } = useTranslation();

	useEffect(() => {
		salesOrderListPublicMutation({});
	}, []);

	const activeOrders = useMemo(() => orders.filter(({ status }) => [OrderStatus.Created, OrderStatus.Prepared, OrderStatus.Paid].includes(status)), [orders]);
	const inactiveOrders = useMemo(() => orders.filter(({ status }) => ![OrderStatus.Created, OrderStatus.Prepared, OrderStatus.Paid].includes(status)), [orders]);
	const isEmptyView = !activeOrders.length && !inactiveOrders.length;

	return <PageWrapper disableFooter={isEmptyView} noScroll={isEmptyView} maxWidth='md'>
		<Box display='flex' flexDirection='column' gap={2} mt={2}>
			<Box display='flex' gap={1}>
				<Typography variant='h6'>{t('salesListView.activeOrders')}</Typography>
				<Chip label={activeOrders.length} />
			</Box>
			{activeOrders.length === 0 && <Box flex={1} display='flex' alignItems='center' justifyContent='center'>
				<Typography fontSize='1.2rem'>{t('salesListView.yourSalesWillShowUpHere')}</Typography>
			</Box>}
			<SimpleList items={activeOrders.map((order) => ({
				imgUrl: order.planImgUrl,
				label: t('salesListView.itemsOrderedBy', {count: order.itemsCount, customerFirstName: order.customerFirstName, customerLastName: order.customerLastName}), component: Link, to: `/sales/${order.id}`,
				secondary: dayjs(order.createdAt).format('MMMM D, HH:mm'),
				properties: [{
					label: `${CURRENCY_SYMBOLS[order.currency]}${(Number(order.total)/100).toFixed(2)}`,
				}],
				content: <Box justifyItems='center' p={1}>
					<Box>
						<Chip label={translateSalesOrderStatus(order.status)} color={order?.status === OrderStatus.Paid ? 'warning' : 'default'} sx={{borderRadius: 0}} />
					</Box>
					<ListViewStatusStepper orderStatus={order.status} isCancelled={[OrderStatus.Cancelled, OrderStatus.Rejected].includes(order?.status)}/>
				</Box>
			}))} />
		</Box>
		<Box display='flex' flexDirection='column' gap={2} mt={5}>
			<Box display='flex' gap={1} alignItems='center'>
				<Typography variant='h6'>{t('salesListView.salesHistory')}</Typography>
				<Chip label={inactiveOrders.length} />
				<IconButton onClick={() => setShowInactive(!showInactive)}>{showInactive ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
			</Box>
			<Collapse in={showInactive && !!inactiveOrders.length}>
				<SimpleList items={inactiveOrders.map((order) => ({
					imgUrl: order.planImgUrl,
					label: t('salesListView.itemsOrderedBy', {count: order.itemsCount, customerFirstName: order.customerFirstName, customerLastName: order.customerLastName}), component: Link, to: `/sales/${order.id}`,
					secondary: dayjs(order.createdAt).format('MMMM D, HH:mm'),
					properties: [{
						label: `${CURRENCY_SYMBOLS[order.currency]}${(Number(order.total)/100).toFixed(2)}`,
					}],
					content: <Box justifyItems='center' p={1}>
						<Box>
							<Chip label={translateSalesOrderStatus(order.status)} color={order?.status === OrderStatus.Shipped ? 'success' : 'default'} sx={{borderRadius: 0}} />
						</Box>
						<ListViewStatusStepper orderStatus={order.status} isCancelled={[OrderStatus.Cancelled, OrderStatus.Rejected].includes(order?.status)}/>
					</Box>
				}))} />
			</Collapse>
		</Box>
	</PageWrapper>;
};

export default SalesListView;
